import DatePicker from '@rio-cloud/rio-uikit/DatePicker';
import TimePicker from '@rio-cloud/rio-uikit/TimePicker';
import type { Moment } from 'moment';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { getIntlFormaTime } from '../../common/utils';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { supportedLocaleMap } from '../../store/lang/lang';
import { selectLocale } from '../../store/lang/langSelectors';
import { selectRouteDeparture } from '../../store/route/routeSelectors';
import { updateRouteDeparture } from '../../store/route/routeSlice';

export const RouteDeparture = () => {
    const dispatch = useAppDispatch();

    const userLocale = useAppSelector(selectLocale);
    const routeDeparture = useAppSelector(selectRouteDeparture);

    const localLang =
        (Object.keys(supportedLocaleMap) as (keyof typeof supportedLocaleMap)[]).find(key => {
            return key !== userLocale && supportedLocaleMap[key] === userLocale;
        }) ?? userLocale;

    const parseStoredDate = (dateString: string): Date => {
        const isoDate = new Date(dateString);
        if (!Number.isNaN(isoDate.getTime())) {
            return isoDate;
        }
        return new Date();
    };

    const [departureDate, setDepartureDate] = useState<Date>(
        routeDeparture.departureDate ? parseStoredDate(routeDeparture.departureDate) : new Date()
    );
    const [departureTime, setDepartureTime] = useState<string>(
        routeDeparture.departureTime || getIntlFormaTime(localLang, new Date())
    );
    const isLocalChange = useRef(false);

    useEffect(() => {
        if (!isLocalChange.current && routeDeparture.departureDate) {
            setDepartureDate(parseStoredDate(routeDeparture.departureDate));
        }
        if (!isLocalChange.current && routeDeparture.departureTime) {
            setDepartureTime(routeDeparture.departureTime);
        }
        isLocalChange.current = false;
    }, [routeDeparture.departureDate, routeDeparture.departureTime, localLang]);

    const handleChangeDepDate = (currentValue: Moment) => {
        isLocalChange.current = true;
        setDepartureDate(currentValue.toDate());
    };

    const handleChangeDepTime = (currentValue: string) => {
        isLocalChange.current = true;
        setDepartureTime(currentValue);
    };

    const handleUseCurrentDateTime = () => {
        isLocalChange.current = true;
        setDepartureDate(new Date());
        setDepartureTime(getIntlFormaTime(localLang, new Date()));
    };

    useEffect(() => {
        if (isLocalChange.current) {
            dispatch(
                updateRouteDeparture({
                    departureDate: departureDate.toISOString(),
                    departureTime,
                })
            );
            isLocalChange.current = false;
        }
    }, [departureDate, departureTime, dispatch]);

    return (
        <div className='align-items-end' data-testid='route-departure'>
            <div className='display-flex align-items-end'>
                <div className='flex-1-1-0 margin-right-5'>
                    <label className='control-label'>
                        <FormattedMessage id='intl-msg:smartRoute.generic.date' />
                    </label>
                    <div data-testid='departure-date-picker'>
                        <DatePicker
                            className='margin-bottom-0'
                            onChange={handleChangeDepDate}
                            value={departureDate}
                            timeFormat={false}
                            locale={localLang}
                        />
                    </div>
                </div>
                <div className='flex-1-1-0 margin-right-5'>
                    <label className='control-label'>
                        <FormattedMessage id='intl-msg:smartRoute.generic.time' />
                    </label>
                    <TimePicker
                        data-testid='departure-time-picker'
                        onChange={handleChangeDepTime}
                        value={departureTime}
                        alwaysShowMask={false}
                    />
                </div>
            </div>
            <button
                type='button'
                className='btn btn-primary btn-link btn-link-inline text-size-12'
                onClick={handleUseCurrentDateTime}
            >
                <FormattedMessage id='intl-msg:smartRoute.generic.useCurrentDateTime' />
            </button>
        </div>
    );
};
